import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import ChartLine from '../../../ChartLine';
import ChartBar from '../../../ChartBar';
import CustomModal from "../../../Modal";
import config from '../../../../config';
import localStorage from '../../../../helpers/localStorage';
import { TranslationContext } from '../../../ChildrenContext';
import CustomTooltip from '../../../Tooltip';

const nameLocalStorage = 'chartType';

import { useTemplateConfig } from '../../../../context/TemplateConfigContext';

const StatisticsChart = ({ chartData: propChartData, groupBy: propGroupBy, parameters, loading, disabledGroupBy: propDisabledGroupBy, onAction, clear }) => {
    const t = useContext(TranslationContext);
    const { assets } = useTemplateConfig();

    if (!localStorage.get(nameLocalStorage)) {
        localStorage.set(nameLocalStorage, config.defaultChartBar);
    }

    const staticNames = t?.chart || {};

    const [state, setState] = useState({
        isActivePanel: true,
        yAxesLeft: 'PublisherCreditTotalAmountConfirmed',
        yAxesRight: 'Visits',
        metrics: [],
        currentGroupBy: '',
        disabledGroupBy: [],
        chartData: propChartData,
        groupBy: propGroupBy,
        yAxesLeftLabelName: 'left',
        yAxesRightLabelName: 'right',
        isVisibleModal: false,
        chartType: localStorage.get(nameLocalStorage)
    });

    useEffect(() => {
        setState(prev => ({
            ...prev,
            currentGroupBy: parameters?.group_by || prev.currentGroupBy,
            chartData: propChartData,
            groupBy: propGroupBy,
            disabledGroupBy: propDisabledGroupBy
        }));
    }, [propChartData, propGroupBy, parameters, propDisabledGroupBy]);

    useEffect(() => {
        if (state.metrics.length > 0 && (state.yAxesLeftLabelName === 'left' || state.yAxesRightLabelName === 'right')) {
            setState(prev => ({
                ...prev,
                yAxesRightLabelName: prev.metrics.find(metric => metric.value === prev.yAxesRight)?.title || prev.yAxesRightLabelName,
                yAxesLeftLabelName: prev.metrics.find(metric => metric.value === prev.yAxesLeft)?.title || prev.yAxesLeftLabelName,
            }));
        }
    }, [state.metrics]);

    useEffect(() => {
        return () => {
            clear && clear();
        };
    }, []);

    const onToggleCollapse = useCallback(() => {
        setState(prev => ({ ...prev, isActivePanel: !prev.isActivePanel }));
    }, []);

    const onChangeDropdownList = useCallback((value, key) => {
        const metricValue = value && typeof value === 'object' ? value.value : value;
        const metricTitle = value && typeof value === 'object' ? value.title : value;
        setState(prev => ({
            ...prev,
            [key]: metricValue,
            [key + 'LabelName']: metricTitle
        }));
    }, []);

    const onChangeGroupBy = useCallback((item) => {
        setState(prev => ({
            ...prev,
            currentGroupBy: item.value
        }));
        onAction && onAction({ ...state, currentGroupBy: item.value });
    }, [onAction, state]);

    const onSetMetrics = useCallback((metrics) => {
        if (metrics && metrics.length > 0) {
            setState(prev => ({
                ...prev,
                metrics,
                yAxesRightLabelName: metrics.find(metric => metric.value === prev.yAxesRight)?.title || prev.yAxesRightLabelName,
                yAxesLeftLabelName: metrics.find(metric => metric.value === prev.yAxesLeft)?.title || prev.yAxesLeftLabelName,
            }));
        } else {
            setState(prev => ({ ...prev, metrics }));
        }
    }, []);

    const onToggleModal = useCallback(() => {
        setState(prev => ({ ...prev, isVisibleModal: !prev.isVisibleModal }));
    }, []);

    const setChartType = useCallback((e, type) => {
        e.preventDefault();
        localStorage.set(nameLocalStorage, type);
        setState(prev => ({ ...prev, chartType: type }));
        onToggleModal();
    }, [onToggleModal]);

    const chartSwitch = useCallback((type) => {
        switch (type) {
            case config.chartLine:
                return (
                    <ChartLine
                        classNameWrapp={'statistics-chart'}
                        chartData={state.chartData}
                        yAxesLeft={state.yAxesLeft}
                        yAxesRight={state.yAxesRight}
                        parameters={parameters}
                        loading={loading}
                        getMetrics={onSetMetrics}
                    />
                );
            default:
                return (
                    <ChartBar
                        classNameWrapp={'statistics-chart'}
                        chartData={state.chartData}
                        yAxesLeft={state.yAxesLeft}
                        yAxesRight={state.yAxesRight}
                        parameters={parameters}
                        loading={loading}
                        getMetrics={onSetMetrics}
                    />
                );
        }
    }, [state.chartData, state.yAxesLeft, state.yAxesRight, parameters, loading, onSetMetrics]);

    const getChartSwitcherIcon = useCallback((type) => {
        switch (type) {
            case config.chartLine:
                return (
                    <img src={assets.chart.line} alt={staticNames.lines}/>
                );
            default:
                return (
                    <img src={assets.chart.bar} alt={staticNames.bars}/>
                );
        }
    }, [staticNames, assets]);

    return (
        <div className="ibox chart">
            <div className="ibox-title ibox-title__included">
                <div className="table-filter table-filter_justify chart-toolbar__wrapper">
                    <div className={`chart-toolbar`}>
                        <div className="table-filter__item">
                            <DropdownList
                                className={'rw-dropdownlist-select dimension dimension__first'}
                                valueField={'value'}
                                textField={'title'}
                                data={state.metrics}
                                value={state.metrics.find(m => m.value === state.yAxesLeft)}
                                disabled={loading || [state.metrics.find(m => m.value === state.yAxesRight)]}
                                onChange={(value) => {
                                    if (value) onChangeDropdownList(value, 'yAxesLeft');
                                }}
                            />
                        </div>
                        <div className="table-filter__item">
                            <DropdownList
                                className={'rw-dropdownlist-select dimension dimension__second'}
                                valueField={'value'}
                                textField={'title'}
                                data={state.metrics}
                                value={state.metrics.find(m => m.value === state.yAxesRight)}
                                disabled={loading || [state.metrics.find(m => m.value === state.yAxesLeft)]}
                                onChange={(value) => {
                                    if (value) onChangeDropdownList(value, 'yAxesRight');
                                }}
                            />
                        </div>
                        <div className="table-filter__item">
                            <DropdownList
                                className={'rw-dropdownlist-select'}
                                valueField={'value'}
                                textField={'title'}
                                data={propGroupBy}
                                value={propGroupBy.find(g => g.value === state.currentGroupBy)}
                                disabled={loading || state.disabledGroupBy}
                                onChange={(value) => {
                                    if (value) onChangeGroupBy(value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={`chart-toolbar`}>
                        <CustomTooltip id={`changeChartTypeTooltip`} tooltip={staticNames.changeChartType}>
                            <button
                                className="btn"
                                onClick={onToggleModal}
                                title={staticNames.changeChartType}
                            >
                                {getChartSwitcherIcon(state.chartType)}
                            </button>
                        </CustomTooltip>
                    </div>
                </div>
            </div>

            <div className="ibox-content">
                <React.Fragment>
                    <CustomModal
                        className={'settingsMenu'}
                        title={staticNames.settingsModalTitle}
                        confirmName={staticNames.applyMetrics}
                        visible={state.isVisibleModal}
                        areNoControls={true}
                        onClose={onToggleModal}
                    >
                        <div className="row">
                            <div className="col-xs-12 chart-types">
                                <button className={`chart-type chart-type-bar ${state.chartType === config.chartBar ? 'active' : ''}`} onClick={e => setChartType(e, config.chartBar)}>
                                    <span>{staticNames.bars}</span>
                                </button>
                                <button
                                    className={`chart-type chart-type-line ${state.chartType === config.chartLine ? 'active' : ''}`}
                                    onClick={e => setChartType(e, config.chartLine)}>
                                    <span>{staticNames.lines}</span>
                                </button>
                            </div>
                        </div>
                    </CustomModal>
                </React.Fragment>
                {chartSwitch(state.chartType)}
            </div>
        </div>
    );
};

StatisticsChart.propTypes = {
    labels: PropTypes.array,
    parameters: PropTypes.object
};

StatisticsChart.defaultProps = {
    labels: [],
    parameters: {}
};

export default StatisticsChart;
